// Toxicity Categorization System
// Classifies objects into TOXIC, SPAM, or DUST categories

// Known scam contracts and malicious addresses
const KNOWN_SCAMS = [
  // Add known scam contract addresses here
  // Example: '0x123...::scam_token::SCAM',
];

const SUSPICIOUS_PATTERNS = [
  'scam',
  'phishing',
  'fake',
  'fraud',
  'rug',
  'honeypot',
];

// Category definitions
export const CATEGORIES = {
  TOXIC: {
    id: 'TOXIC',
    name: 'Toxic',
    icon: '🔴',
    color: '#ef4444',
    description: 'Known scams, malicious contracts, or dangerous objects',
    priority: 1, // Highest priority
  },
  SPAM: {
    id: 'SPAM',
    name: 'Spam',
    icon: '🟡',
    color: '#eab308',
    description: 'Unsolicited airdrops, marketing tokens, or unwanted items',
    priority: 2,
  },
  DUST: {
    id: 'DUST',
    name: 'Dust',
    icon: '⚪',
    color: '#9ca3af',
    description: 'Low-value balances, failed swap remnants, or negligible amounts',
    priority: 3, // Lowest priority
  },
};

// Dust threshold in SUI
const DUST_THRESHOLD = 0.01; // Less than 0.01 SUI is considered dust

/**
 * Categorize an object based on various criteria
 * @param {Object} obj - The object to categorize
 * @returns {Object} - Category info with reason
 */
export function categorizeObject(obj) {
  // 1. Check if it's a known scam (HIGHEST PRIORITY)
  const scamCheck = checkForScam(obj);
  if (scamCheck.isScam) {
    return {
      category: CATEGORIES.TOXIC,
      reason: scamCheck.reason,
      riskLevel: 'HIGH',
      autoDetected: true,
    };
  }

  // 2. Check if it's dust (LOW BALANCE)
  const dustCheck = checkForDust(obj);
  if (dustCheck.isDust) {
    return {
      category: CATEGORIES.DUST,
      reason: dustCheck.reason,
      balance: obj.balance || 0,
      autoDetected: true,
    };
  }

  // 3. Check if it's spam (AIRDROP/UNWANTED)
  const spamCheck = checkForSpam(obj);
  if (spamCheck.isSpam) {
    return {
      category: CATEGORIES.SPAM,
      reason: spamCheck.reason,
      autoDetected: true,
    };
  }

  // 4. Default to SPAM (user-selected unwanted item)
  return {
    category: CATEGORIES.SPAM,
    reason: 'User marked as unwanted',
    autoDetected: false,
  };
}

/**
 * Check if object is a known scam
 */
function checkForScam(obj) {
  // Check against known scam list
  const objectType = obj.data?.type || obj.type || '';
  
  if (KNOWN_SCAMS.includes(objectType)) {
    return {
      isScam: true,
      reason: 'Known scam contract',
    };
  }

  // Check for suspicious patterns in name or type
  const objectName = obj.data?.display?.data?.name || obj.name || '';
  const combinedText = `${objectName} ${objectType}`.toLowerCase();

  for (const pattern of SUSPICIOUS_PATTERNS) {
    if (combinedText.includes(pattern)) {
      return {
        isScam: true,
        reason: `Suspicious pattern detected: "${pattern}"`,
      };
    }
  }

  return { isScam: false };
}

/**
 * Check if object is dust (low value)
 */
function checkForDust(obj) {
  const balance = obj.balance || 0;
  
  if (balance > 0 && balance < DUST_THRESHOLD) {
    return {
      isDust: true,
      reason: `Low balance (${balance.toFixed(6)} SUI)`,
    };
  }

  // Check for common dust indicators in name
  const objectName = (obj.data?.display?.data?.name || obj.name || '').toLowerCase();
  if (objectName.includes('dust') || objectName.includes('remnant') || objectName.includes('fragment')) {
    return {
      isDust: true,
      reason: 'Identified as dust token',
    };
  }

  return { isDust: false };
}

/**
 * Check if object is spam (airdrop/unwanted)
 */
function checkForSpam(obj) {
  const objectName = (obj.data?.display?.data?.name || obj.name || '').toLowerCase();
  const objectType = (obj.data?.type || obj.type || '').toLowerCase();

  // Common spam indicators
  const spamPatterns = [
    'airdrop',
    'free',
    'claim',
    'reward',
    'bonus',
    'gift',
    'promo',
    'marketing',
  ];

  for (const pattern of spamPatterns) {
    if (objectName.includes(pattern) || objectType.includes(pattern)) {
      return {
        isSpam: true,
        reason: `Spam pattern detected: "${pattern}"`,
      };
    }
  }

  return { isSpam: false };
}

/**
 * Batch categorize multiple objects
 */
export function categorizeObjects(objects) {
  return objects.map(obj => ({
    ...obj,
    categoryInfo: categorizeObject(obj),
  }));
}

/**
 * Get category breakdown for a list of objects
 */
export function getCategoryBreakdown(objects) {
  const breakdown = {
    TOXIC: 0,
    SPAM: 0,
    DUST: 0,
  };

  objects.forEach(obj => {
    const category = obj.categoryInfo?.category?.id || 'SPAM';
    breakdown[category]++;
  });

  return breakdown;
}

/**
 * Filter objects by category
 */
export function filterByCategory(objects, categoryId) {
  return objects.filter(obj => 
    obj.categoryInfo?.category?.id === categoryId
  );
}

/**
 * Sort objects by category priority (TOXIC first, then SPAM, then DUST)
 */
export function sortByPriority(objects) {
  return [...objects].sort((a, b) => {
    const priorityA = a.categoryInfo?.category?.priority || 999;
    const priorityB = b.categoryInfo?.category?.priority || 999;
    return priorityA - priorityB;
  });
}

/**
 * Get risk summary for selected objects
 */
export function getRiskSummary(objects) {
  const breakdown = getCategoryBreakdown(objects);
  const total = objects.length;

  return {
    total,
    breakdown,
    hasToxic: breakdown.TOXIC > 0,
    hasHighRisk: breakdown.TOXIC > total * 0.5, // More than 50% toxic
    riskLevel: breakdown.TOXIC > 0 ? 'HIGH' : breakdown.SPAM > 0 ? 'MEDIUM' : 'LOW',
  };
}
